import React from "react"

import { MdCircle, MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md"
import { getColors, MobergButton, MobergColumn, MobergHeader, MobergIconSize, MobergRow, MobergText, MobergTheme } from "../../../../../../Moberg"
import { Gateway, GatewayStatus } from "../../Types/Gateway"

import { useMobergMenu } from "../../../../../../Hooks/useMobergMenu"
import { Deployment, useEnvironmentVariablesProvider } from "../../../../../../Providers/EnvironmentVariablesProvider"
import { Device, DeviceStatus } from "../../Types/Device"
import { currentPatientFileInfoAtom } from "../../Atoms/PatientFile"
import { useRecoilValue } from "recoil"
import { useMCPDeviceStatus } from "../../Hooks/useMCPDeviceStatus"

type MCPDeviceStatusProps = {
    dataObjectId: number
}

export const MCPDeviceStatus: React.FC<MCPDeviceStatusProps> = () => {
    const { isAdmitted, gatewayId } = useRecoilValue(currentPatientFileInfoAtom)
    const { gateway, devices } = useMCPDeviceStatus({ gatewayId })

    const { deployment } = useEnvironmentVariablesProvider()
    const menu = useMobergMenu({ placement: "bottom-end", useAnchorAsMinWidth: true })

    if (!isAdmitted || deployment !== Deployment.CLINICAL || gatewayId === undefined) {
        return null
    }

    const DeviceMenu = () => {
        if (devices.length === 0) {
            return (
                <MobergRow style={{ padding: "4px" }} horizontalAlign="center">
                    <MobergText> No devices </MobergText>
                </MobergRow>
            )
        }

        return (
            <MobergColumn style={{ padding: "8px" }} gap="16px">
                <MobergHeader>Devices</MobergHeader>
                
                <MobergColumn gap="4px">
                    {devices.map(device => (
                        <MobergRow key={device.id} style={{ padding: "8px", borderTop: "1px solid lightgray" }} gap="32px" horizontalAlign="space-between">
                            <MobergText>{device.type}</MobergText>

                            <MobergRow gap="8px" expand={false}>
                                <MobergText style={{ whiteSpace: "nowrap" }}>{device.status}</MobergText>
                                <MdCircle size={MobergIconSize.X_SMALL} color={getDeviceStatusColor(device)} />
                            </MobergRow>
                        </MobergRow>
                    ))}
                </MobergColumn>
            </MobergColumn>
        )
    }

    return (
        <>
            <MobergButton onClick={menu.open} style={{ border: "1px solid #cdcdcd", paddingRight: "8px" }}>
                <MobergRow gap="4px">
                    {gateway
                        ? (<MobergRow gap="32px">
                                <MobergText>{gateway.name}</MobergText>
                                <MobergText>{gateway.status}</MobergText>
                            </MobergRow>
                        )
                        : <MobergText>Not connected</MobergText>
                    }
                    <MdCircle size={MobergIconSize.X_SMALL} color={getGatewayStatusColor(gateway)} />
                </MobergRow>

                <span style={{ pointerEvents: "none" }}>
                    {menu.isOpen ? <MdKeyboardArrowUp size={MobergIconSize.REGULAR} /> : <MdKeyboardArrowDown size={MobergIconSize.REGULAR} />}
                </span>
            </MobergButton>

            <menu.MobergMenu>
                <DeviceMenu />
            </menu.MobergMenu>
        </>
    )
}

const getGatewayStatusColor = (gateway?: Gateway) => {
    switch (gateway?.status) {
        case GatewayStatus.ACTIVE:
            return getColors(MobergTheme.GREEN).main
        case GatewayStatus.PAUSED:
        case GatewayStatus.PAUSING:
        case GatewayStatus.STARTING:
        case GatewayStatus.STOPPING:
            return "orange" // getColors(MobergTheme.WARNING).main
        case GatewayStatus.STOPPED:
        default:
            return getColors(MobergTheme.RED).main
    }
}

const getDeviceStatusColor = (gateway?: Device) => {
    switch (gateway?.status) {
        case DeviceStatus.ACTIVE:
            return getColors(MobergTheme.GREEN).main
        case DeviceStatus.PAUSED:
            return "orange" // getColors(MobergTheme.WARNING).main
        case DeviceStatus.ERROR:
        default:
            return getColors(MobergTheme.RED).main
    }
}